import {useEffect,useState} from 'react'
import {useSelector,useDispatch} from 'react-redux'

import {
	saveClass,
	getClasses,
	updateEditor,
	updateLiveClass,
	selectShowEditor,
	selectClassToEdit,
} from '../liveClassesSlice'
import {
	dateToAPIStandard,
	envAwarePost
} from '../../../util/util'
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import Select from "react-select"
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import Tooltip from 'react-bootstrap/Tooltip'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import useApiStatusDispatch from '../../../hooks/useApiStatusDispatch'
import useCurrencySettings from '../../../hooks/useCurrencySettings'
import {selectCurrencyById} from '../../settings/settingsSlice'
import {getCurrencySymbol} from '../../../util/intl'

const Editor = ()=>{
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch();
	const [status,setStatus] = useState('idle')
	const show = useSelector(selectShowEditor)
	const classToEdit = useSelector(selectClassToEdit)
	const onHide = ()=>{
		dispatch(updateEditor({show:false,id:null}))
	}
	const isTime24Hour = Number(useSelector(state => state.users.current_user.is_24_hour_time))

	const {
		acceptedCurrency:defaultAcceptedCurrency,
		acceptedCurrencies
	} = useCurrencySettings()

	/* ---- form state ---- */
	const [acceptedCurrencyForClass,setAcceptedCurrencyForClass] = useState(defaultAcceptedCurrency.id)
	const classCurrencyData = useSelector(state=>selectCurrencyById(state,acceptedCurrencyForClass))
	const [selectedTrainingTypes,setSelectedTrainingTypes] = useState([])
	const [selectedEquipment,setSelectedEquipment] = useState([])
	const [advancedPricing,setAdvancedPricing] = useState(false)
	const [clientFree,setClientFree] = useState(false)
	const [publicFree,setPublicFree] = useState(false)
	const [clientPrice,setClientPrice] = useState('')
	const [publicPrice,setPublicPrice] = useState('')
	const [description,setDescription] = useState('')
	const [isPublic,setIsPublic] = useState(false)
	const [duration,setDuration] = useState('')
	const [maxSize,setMaxSize] = useState('')
	const [free,setFree] = useState(false)
	const [price,setPrice] = useState('')
	const [title,setTitle] = useState('')
	const [date,setDate] = useState('')
	const [link,setLink] = useState('')

	/* ---- fixed state ---- */ 
	const trainingTypes = ['Yoga','HIIT','Cardio','Dance','Strength','Other']
	const [equipment,setEquipment] = useState([])

	useEffect(()=>{
		envAwarePost('api/events/api/',{
			action:'getAdditionalExerciseData'
		}).then(res=>{
			if(res.success === '1'){
				setEquipment(res.data.equipment)
			}else{
				alert(res.message)
			}
		})
	},[])

	const clearForm = ()=>{
		setSelectedTrainingTypes([])
		setAdvancedPricing(false)
		setSelectedEquipment([])
		setPublicFree(false)
		setClientFree(false)
		setDate(new Date())
		setIsPublic(false)
		setPublicPrice('')
		setClientPrice('')
		setDescription('')
		setDuration('')
		setFree(false)
		setMaxSize('')
		setTitle('')
		setPrice('')
		setLink('')
	}

	useEffect(()=>{
		//prefil form else clear form
		if(classToEdit){
			const equipment = classToEdit.equipment.filter(e=>Boolean(e))
			const equipment_ids = classToEdit.equipment_ids.filter(e=>Boolean(e))
			setSelectedEquipment(equipment.map((name,i)=>({
				value:equipment_ids[i],
				label:name,
			})))
			setAdvancedPricing(classToEdit.advancedPricing === '1')
			setSelectedTrainingTypes(classToEdit.trainingTypes.map(tt=>({
				value:tt,
				label:tt,
			})))
			setAcceptedCurrencyForClass(classToEdit.presentment_currency_id)
			setPublicFree(classToEdit.publicFree === '1')
			setClientFree(classToEdit.clientFree === '1')
			setIsPublic(classToEdit.isPublic === '1')
			setDate(new Date(classToEdit?.startTime?.split(' ').join('T')))
			setPublicPrice(classToEdit.publicPrice)
			setClientPrice(classToEdit.clientPrice)
			setDescription(classToEdit.description)
			setLink(classToEdit.link)
			setDuration(classToEdit.duration)
			setFree(classToEdit.free === '1')
			setMaxSize(classToEdit.max_size)
			setTitle(classToEdit.title)
			setPrice(classToEdit.price)
		}else{
			clearForm()
		}
	},[classToEdit])

	/* ---- handlers ---- */

	const saveHandler = ()=>{
		setStatus('pending')
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone

		const payload = {
			trainingType:selectedTrainingTypes.map( tt => tt.value ),
			equipment:selectedEquipment.map( e => e.value ),
			presentment_currency_id:acceptedCurrencyForClass,
			//subtract one to account for trainer & default to 20 if not set
			max_size:Boolean(maxSize)?maxSize-1:20,
			advancedPricing:advancedPricing?1:0,
			datetime:dateToAPIStandard(date),
			publicFree:publicFree?1:0,
			clientFree:clientFree?1:0,
			isPublic:isPublic?1:0,
			_id:classToEdit?._id,
			free:free?1:0,
			description,
			publicPrice,
			clientPrice,
			duration,
			title,
			price,
			tz:tz,
			link
		}
		//if classToEdit is set then we want to update
		//else we are creating a new class. Set the correct
		//action creator here
		const actionCreator = (
			Boolean(classToEdit) ?
			updateLiveClass :
			saveClass
		)
		let operation = Boolean(classToEdit) ? "update" : "create"
		statusDispatch(actionCreator(payload),"class",operation)
		.then(()=>{
			return dispatch(getClasses())
		}).then(()=>{
			setStatus('idle')
			onHide()
			clearForm()
		}).catch(()=>{
			setStatus('idle')
		})
	}

	const handleUpdateCurrency = e => {
        const currency_id = e.target.value
        setAcceptedCurrencyForClass(currency_id)
    }

	/* ---- Validation ---- */

	const generateValidationErrors = ()=>{
		const errors = []
		if(!title) errors.push('title')
		if(!date) errors.push('date')
		if(!duration) errors.push('duration')
		if(
			(advancedPricing&&((!clientPrice&&!clientFree)||(!publicPrice&&!publicFree)))||
			(!advancedPricing&&(!price&&!free))
		){
			errors.push('pricing')
		}
		return errors
	}

	const validationErrors = generateValidationErrors()

	return (
		<Modal show={show} onHide={onHide} size="xl">
			<Modal.Header closeButton>
				Live Class Editor
			</Modal.Header>
			<Modal.Body>
				<Row>
					<Col lg={6}>
						<strong>Info</strong>
						<Form.Control 
							onChange={e=>{setTitle(e.target.value)}}
							placeholder="Title"
							className="mb-1"
							value={title} 
						/>
						<Form.Control 
							onChange={e=>{setDescription(e.target.value)}}
							placeholder="Description"
							className="mb-1"
							value={description}
							as="textarea" 
						/>
						<Form.Control 
							onChange={e=>{setLink(e.target.value)}}
							placeholder="Link"
							value={link}
						/>
						<strong>Scheduling</strong>
						<div>
							<DatePicker 
								dateFormat={isTime24Hour ? "MMM d, yyyy HH:mm" :"MMM d yyyy, h:mm aa"}
								onChange={date=>setDate(date)}
								customInput={<Form.Control/>}
								timeIntervals={5}
								className="mb-1"
								selected={date}
								showTimeSelect
								timeFormat={isTime24Hour ? "HH:mm" : "p"}
							/>
						</div>
						<Form.Control 
							onChange={e=>{setDuration(e.target.value)}}
							placeholder="Duration"
							value={duration} 
						/>
						<div>
							<small>
								Duration is in minutes (max 120) 
								<OverlayTrigger overlay={
									<Tooltip>
										Every class gets 10 extra minutes at start and end for prep/wrap-up
									</Tooltip>
								}>
									<i className="ml-1 fas fa-info-circle"/>
								</OverlayTrigger>
							</small>
						</div>
						<strong>Equipment</strong>
						<Select 
							onChange={selectedOptions=>{
								setSelectedEquipment(selectedOptions)
							}}
							options={equipment.map(
								e=>({value:e.id,label:e.name})
							)} 
							value={selectedEquipment}
							isMulti
						/>
					</Col>
					<Col lg={6}>
						<div className="d-flex justify-content-between align-items-center">
							<strong>Training Type</strong>
							<small>(max of 2)</small>
						</div>
						<Select 
							options={selectedTrainingTypes.length === 2 ? [] : trainingTypes.map(
								t=>({value:t,label:t})
							)}
							noOptionsMessage={() => {
								return selectedTrainingTypes.length === 2 ? 'Max of 2' : 'No options available' ;
							}}
							onChange={selectedOptions=>{
								setSelectedTrainingTypes(selectedOptions)
							}}
							value={selectedTrainingTypes}
							isMulti
						/>
						<strong className="d-block">Participant Settings</strong>
						<small>Max size is 200 (199 clients + 1 trainer)</small>
						<div className="input-group">
							<Form.Control 
								onChange={ e =>{ setMaxSize(e.target.value) } }
								placeholder="max size"
								value={maxSize}
							/>
							<div className="input-group-append">
								<label className="d-flex align-items-center input-group-text">
									Public <Form.Check checked={isPublic} onChange={e=>{setIsPublic(e.target.checked)}}/>
								</label>
							</div>
						</div>
						<small>
							Public classes allow non-clients to sign-up and pay for your classes. People can find public classes at 
							<a href="https://findtraingain.com" target="_blank" className="ml-1">FindTrainGain.</a>
						</small>
						<div>
							<strong className="mr-2">Pricing</strong>
							<OverlayTrigger overlay={
								<Tooltip>
									Payments will be deposited into your account entered during your payments setup.
								</Tooltip>
							}>
								<i className="fas fa-info-circle"/>
							</OverlayTrigger>
						</div>
						<Form.Switch 
							checked={advancedPricing}
							onChange={(e)=>{setAdvancedPricing(e.target.checked)}}
							label="Advanced Pricing"
							id="advanced-pricing-switch" 
						/>
						{
							!advancedPricing ?
							<div>
								<div className="input-group">
									<Form.Control 
										onChange={handleUpdateCurrency}
										className="input-group-prepend"
										value={acceptedCurrencyForClass}
										style={{maxWidth:'100px'}}
										as="select"
									>
										{acceptedCurrencies.map(ac=>(
	                                        <option key={ac.id} value={ac.id}>
	                                            {
	                                            	getCurrencySymbol(ac.ISO) === ac.ISO ?
	                                            	ac.ISO :
	                                            	getCurrencySymbol(ac.ISO)
	                                            }
	                                        </option>
	                                    ))}
									</Form.Control>
									<Form.Control 
										onChange={e=>{setPrice(e.target.value)}}
										placeholder={`Price in ${String.fromCharCode(classCurrencyData?.char_code)}`}
										value={price}
									/>
									<div className="input-group-append">
										<label className="d-flex align-items-center input-group-text">
											Free <Form.Check checked={free} onChange={e=>{setFree(e.target.checked)}}/>
										</label>
									</div>
								</div>
							</div>
							:
							<div>
								<Form.Control 
									onChange={handleUpdateCurrency}
									className="input-group-prepend"
									value={acceptedCurrencyForClass}
									as="select"
								>
									{acceptedCurrencies.map(ac=>(
                                        <option key={ac.id} value={ac.id}>
                                            {String.fromCharCode(ac.char_code)} {ac.ISO}
                                        </option>
                                    ))}
								</Form.Control>
								<small>Client</small>
								<div className="input-group">
									<Form.Control 
										onChange={e=>{setClientPrice(e.target.value)}}
										placeholder={`Price in ${String.fromCharCode(classCurrencyData?.char_code)}`}
										value={clientPrice}
									/>
									<div className="input-group-append">
										<label className="d-flex align-items-center input-group-text">
											Free <Form.Check checked={clientFree} onChange={e=>{setClientFree(e.target.checked)}}/>
										</label>
									</div>
								</div>
								<small>Public</small>
								<div className="input-group">
									<Form.Control 
										onChange={e=>{setPublicPrice(e.target.value)}}
										placeholder={`Price in ${String.fromCharCode(classCurrencyData?.char_code)}`}
										value={publicPrice}
									/>
									<div className="input-group-append">
										<label className="d-flex align-items-center input-group-text">
											Free <Form.Check checked={publicFree} onChange={e=>{setPublicFree(e.target.checked)}}/>
										</label>
									</div>
								</div>
							</div>
						}
					</Col>
				</Row>
				<div className="d-flex justify-content-end mt-2 align-items-center">
					{validationErrors.length > 0 &&
						<span className="text-primary mr-2">
							Provide: {validationErrors.join(', ')}
						</span>
					}
					<Button 
						disabled={status==='pending'||validationErrors.length}
						onClick={saveHandler} 
						variant="success" 
						size="sm"
					>
						Save Class {status==='pending'&&<Spinner animation="border" size="sm"/>}
					</Button>
				</div>
			</Modal.Body>
		</Modal>
	)
}

export default Editor